import { useEffect, useRef, useState, useCallback } from 'react';
import { ScanProgress, ScanUpdateEvent, WebSocketMessage } from '../types';

interface WebSocketHookReturn {
  socket: WebSocket | null;
  isConnected: boolean;
  lastMessage: any;
  sendMessage: (message: any) => void;
  reconnect: () => void;
}

// Connection states to prevent race conditions
enum ConnectionState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  DISCONNECTING = 'disconnecting',
  RECONNECTING = 'reconnecting'
}

export const useWebSocket = (url?: string): WebSocketHookReturn => {
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<any>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectInterval = useRef<NodeJS.Timeout>();
  const reconnectDelay = useRef(1000); // Start with 1 second delay
  const connectionState = useRef<ConnectionState>(ConnectionState.DISCONNECTED);
  const isInitialized = useRef(false); // Prevent React StrictMode double initialization
  const currentSocket = useRef<WebSocket | null>(null); // Track current socket instance

  // Build WebSocket URL with JWT token for authentication - called fresh each connect
  const getAuthenticatedWsUrl = useCallback(() => {
    const baseUrl = url || `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;
    const token = localStorage.getItem('auth_token');
    if (token) {
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}token=${encodeURIComponent(token)}`;
    }
    return null; // No token = don't connect
  }, [url]);

  const disconnect = useCallback(() => {
    connectionState.current = ConnectionState.DISCONNECTING;

    // Clear any pending reconnection attempts
    if (reconnectInterval.current) {
      clearTimeout(reconnectInterval.current);
      reconnectInterval.current = undefined;
    }

    // Clean up current socket
    if (currentSocket.current) {
      // Remove event listeners to prevent callbacks
      currentSocket.current.onopen = null;
      currentSocket.current.onmessage = null;
      currentSocket.current.onclose = null;
      currentSocket.current.onerror = null;

      // Close with intentional disconnect code
      if (currentSocket.current.readyState === WebSocket.OPEN ||
          currentSocket.current.readyState === WebSocket.CONNECTING) {
        currentSocket.current.close(1000, 'Client disconnect');
      }
      currentSocket.current = null;
    }

    connectionState.current = ConnectionState.DISCONNECTED;
    setSocket(null);
    setIsConnected(false);
    reconnectAttempts.current = maxReconnectAttempts; // Prevent auto-reconnect
  }, []);

  const connect = useCallback(() => {
    // Prevent multiple simultaneous connection attempts
    if (connectionState.current === ConnectionState.CONNECTING ||
        connectionState.current === ConnectionState.CONNECTED) {
      return;
    }

    // SECURITY: Don't connect if no auth token exists
    const wsUrl = getAuthenticatedWsUrl();
    if (!wsUrl) {
      return;
    }

    try {
      connectionState.current = ConnectionState.CONNECTING;

      // Properly close existing connection if any
      if (currentSocket.current) {
        currentSocket.current.onopen = null;
        currentSocket.current.onmessage = null;
        currentSocket.current.onclose = null;
        currentSocket.current.onerror = null;

        if (currentSocket.current.readyState === WebSocket.OPEN ||
            currentSocket.current.readyState === WebSocket.CONNECTING) {
          currentSocket.current.close(1000, 'New connection requested');
        }
        currentSocket.current = null;
      }

      const ws = new WebSocket(wsUrl);
      currentSocket.current = ws;

      ws.onopen = () => {
        // Verify this is still the current socket (prevent race conditions)
        if (currentSocket.current !== ws) {
          ws.close();
          return;
        }

        connectionState.current = ConnectionState.CONNECTED;
        setIsConnected(true);
        setSocket(ws);
        reconnectAttempts.current = 0;
        reconnectDelay.current = 1000; // Reset delay

        // Clear any pending reconnection attempts
        if (reconnectInterval.current) {
          clearTimeout(reconnectInterval.current);
          reconnectInterval.current = undefined;
        }
      };

      ws.onmessage = (event) => {
        // Verify this is still the current socket
        if (currentSocket.current !== ws) {
          return;
        }

        try {
          const message = JSON.parse(event.data);

          setLastMessage(message);

          // Dispatch generic websocket message event that components listen for
          const genericEvent = new CustomEvent('websocket_message', {
            detail: message,
            bubbles: true
          });
          window.dispatchEvent(genericEvent);

          // Also dispatch specific event types for backwards compatibility
          if (message.type) {
            const specificEvent = new CustomEvent(message.type, {
              detail: message,
              bubbles: true
            });
            window.dispatchEvent(specificEvent);
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      ws.onclose = (event) => {
        // Verify this is still the current socket
        if (currentSocket.current !== ws) {
          return;
        }

        connectionState.current = ConnectionState.DISCONNECTED;
        setIsConnected(false);
        setSocket(null);
        currentSocket.current = null;

        // SECURITY: Don't reconnect if no auth token (user logged out)
        const token = localStorage.getItem('auth_token');
        if (!token) {
          return;
        }

        // Only attempt to reconnect if not intentionally closed and within retry limit
        if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
          connectionState.current = ConnectionState.RECONNECTING;
          const delay = Math.min(reconnectDelay.current * Math.pow(1.5, reconnectAttempts.current), 30000); // Max 30s

          reconnectInterval.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        } else if (reconnectAttempts.current >= maxReconnectAttempts) {
          console.error('Max reconnection attempts reached. WebSocket connection failed permanently.');
        }
      };

      ws.onerror = (error) => {
        // Verify this is still the current socket
        if (currentSocket.current !== ws) {
          return;
        }

        console.error('WebSocket error:', error);
        connectionState.current = ConnectionState.DISCONNECTED;
        setIsConnected(false);
      };

      // Don't set socket state until connection is established (prevents race conditions)

    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      connectionState.current = ConnectionState.DISCONNECTED;
      setIsConnected(false);

      // SECURITY: Don't retry if no auth token
      const token = localStorage.getItem('auth_token');
      if (!token) {
        return;
      }

      // Retry after delay if we haven't exceeded max attempts
      if (reconnectAttempts.current < maxReconnectAttempts) {
        connectionState.current = ConnectionState.RECONNECTING;
        const delay = Math.min(reconnectDelay.current * Math.pow(1.5, reconnectAttempts.current), 30000);

        reconnectInterval.current = setTimeout(() => {
          reconnectAttempts.current++;
          connect();
        }, delay);
      }
    }
  }, [getAuthenticatedWsUrl]);

  const sendMessage = useCallback((message: any) => {
    if (currentSocket.current && currentSocket.current.readyState === WebSocket.OPEN) {
      try {
        const messageStr = typeof message === 'string' ? message : JSON.stringify(message);
        currentSocket.current.send(messageStr);
      } catch (error) {
        console.error('Failed to send WebSocket message:', error);
      }
    } else {
      console.warn('WebSocket not ready for sending. Message not sent:', message);
    }
  }, [isConnected]);

  const reconnect = useCallback(() => {
    disconnect();
    setTimeout(() => {
      reconnectAttempts.current = 0;
      reconnectDelay.current = 1000;
      connect();
    }, 1000);
  }, [connect, disconnect]);

  // Initialize connection - Prevent React StrictMode double initialization
  useEffect(() => {
    if (isInitialized.current) {
      return;
    }

    isInitialized.current = true;

    // Only connect if we have a token
    const token = localStorage.getItem('auth_token');
    if (token) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      isInitialized.current = false;
      disconnect();
    };
  }, []); // No dependencies - connect once on mount

  // SECURITY: Listen for auth events to disconnect/reconnect
  useEffect(() => {
    const handleLogout = () => {
      disconnect();
    };

    const handleLogin = () => {
      // Reset reconnect state and connect with fresh token
      reconnectAttempts.current = 0;
      reconnectDelay.current = 1000;
      connectionState.current = ConnectionState.DISCONNECTED;
      connect();
    };

    window.addEventListener('auth_logout', handleLogout);
    window.addEventListener('auth_login', handleLogin);

    return () => {
      window.removeEventListener('auth_logout', handleLogout);
      window.removeEventListener('auth_login', handleLogin);
    };
  }, [connect, disconnect]);

  return {
    socket,
    isConnected,
    lastMessage,
    sendMessage,
    reconnect,
  };
};

// Simplified scan updates hook - with progress throttle to prevent excessive re-renders
export const useScanUpdates = (scanId?: number) => {
  const { isConnected } = useWebSocket();
  const [scanProgress, setScanProgress] = useState<ScanProgress | null>(null);
  const [scanStatus, setScanStatus] = useState<string | null>(null);
  const lastProgressUpdate = useRef(0);
  const handlersRef = useRef<{
    consolidatedHandler: EventListener;
  } | null>(null);

  useEffect(() => {
    // Use a single consolidated handler to reduce event listener count
    const consolidatedHandler = (event: CustomEvent) => {
      const message = event.detail;

      if (message.type === 'scan_update' && message.data) {
        const update = message.data;
        if (!scanId || update.scan_id === scanId) {
          setScanStatus(update.status);
        }
      } else if (message.type === 'scan_progress' && message.data) {
        const progressData = message.data;
        if (!scanId || progressData.scan_id === scanId) {
          // Throttle progress updates to at most once per 2 seconds
          const now = Date.now();
          if (now - lastProgressUpdate.current < 2000) return;
          lastProgressUpdate.current = now;

          // Normalize progress field - agent sends 'progress', local scans send 'overall_progress'
          const normalizedProgress = {
            ...progressData,
            overall_progress: progressData.overall_progress ?? progressData.progress ?? 0
          };
          setScanProgress(normalizedProgress);
        }
      } else if (message.type === 'scan_completion' && message.data) {
        const completionData = message.data;
        if (!scanId || completionData.scan_id === scanId) {
          setScanStatus(completionData.status);
        }
      }
    };

    // Store handler for cleanup
    handlersRef.current = {
      consolidatedHandler: consolidatedHandler as EventListener
    };

    // Listen for WebSocket messages with single handler
    window.addEventListener('websocket_message', consolidatedHandler as EventListener);

    return () => {
      window.removeEventListener('websocket_message', consolidatedHandler as EventListener);
      handlersRef.current = null;
    };
  }, [scanId]);

  return {
    scanProgress,
    scanStatus,
    isConnected,
  };
};
